'use client';

import { Link } from '@/components/ui/Link';
import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { ApplicationLogo } from '@/ui/images/ApplicationLogo';
import { logger } from '@/utils/loggerUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthentication } from '@vimond/features/authentication';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export const SignUpConfirmForm = () => {
  const { t } = useTranslation();

  const { confirmSignUp, resendSignUpCode } = useAuthentication();
  const [errorMessage, setErrorMessage] = useState<string>();
  const FormSchema = z.object({
    email: z.string().email(),
    confirmationCode: z.string(),
  });

  type FormSchemaType = z.infer<typeof FormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    getValues,
    handleSubmit,
    register,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    values: {
      email: '',
      confirmationCode: '',
    },
  });

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const { confirmationCode, email } = data;
    try {
      const res = await confirmSignUp({ email, confirmationCode });
      if (res.isSignUpComplete) {
        window.location.href = '/auth/signin';
      }
    } catch (error) {
      logger.error(error);
      setErrorMessage(t('auth.invalid-confirmation-code-or-email'));
    }
  };

  const email = getValues('email');
  return (
    <section>
      <div className="mx-auto flex flex-col items-center justify-center px-6 py-8 lg:h-screen lg:py-0">
        <ApplicationLogo />
        <div className="border-background-border bg-background-secondary w-full rounded-lg border shadow sm:max-w-md md:mt-0 xl:p-0">
          <div className="space-y-4 p-6 sm:p-8 md:space-y-6">
            <h1 className="text-center text-xl font-bold leading-tight tracking-tight md:text-2xl">
              {t('auth.confirm-your-account')}
            </h1>

            <form
              className="space-y-4 md:space-y-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <Label htmlFor="email">{t('auth.email-address')}</Label>
                <div className="mt-2">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register('email')}
                    placeholder={t('auth.email-placeholder')}
                    aria-describedby="email-error"
                  />
                </div>
                <p
                  id="email-error"
                  className="text-red-500"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {errors.email?.message}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="confirmationCode">{t('auth.code')}</Label>
                  </div>
                  <div className="text-sm">
                    <button
                      type="button"
                      disabled={
                        email?.length === 0 ||
                        errors.email?.message !== undefined
                      }
                      onClick={async () => {
                        try {
                          await resendSignUpCode(email);
                          setErrorMessage(t('auth.code-resent'));
                        } catch (error) {
                          logger.error(error);
                          setErrorMessage(t('auth.failed-to-resend-code'));
                        }
                      }}
                      className="text-text-highlight cursor-pointer font-semibold disabled:opacity-50"
                    >
                      {t('auth.resend-code')}
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  <Input
                    id="confirmationCode"
                    type="text"
                    autoComplete="current-code"
                    {...register('confirmationCode')}
                    placeholder={t('auth.check-your-email-for-the-code')}
                    aria-describedby="confirmationCode-error"
                  />
                </div>
                <p
                  id="confirmationCode-error"
                  className="text-red-500"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {errors.confirmationCode?.message}
                </p>
              </div>

              <Button
                variant="primary"
                size="sm"
                disabled={isLoading || isSubmitting || !isDirty}
                type="submit"
                className="w-full"
              >
                {t('auth.confirm-your-account')}
              </Button>

              {errorMessage && <p className="text-red-500">{errorMessage}</p>}
            </form>

            <p className="text-text-secondary mt-10 text-center text-sm">
              {t('auth.have-an-account')}{' '}
              <Link
                href="/auth/signin"
                className="text-text-highlight font-semibold leading-6"
              >
                {t('auth.sign-in-now')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
